import type { components } from '../api/portal-api/portal-api-definition';
import { assertUnreachable } from './exhaustiveness-check';
import type { NloApiError } from '#imports';
import { NloError } from '#imports';

export const GENERIC_ERROR_MESSAGE = 'Er is iets mis gegaan, probeer het later opnieuw';
export const RECAPTCHA_MISSING_ERROR_MESSAGE = 'Je moet de reCaptcha nog invullen.';
export const NETWORK_ERROR_MESSAGE = 'Geen internetverbinding, bekijk je instellingen';
export const ABORT_ERROR_MESSAGE = 'Actie geannuleerd, probeer het opnieuw';
export const NOT_AUTHENTICATED_ERROR_MESSAGE = 'Je bent niet ingelogd';

/**
 * Represents a user that is not logged in.
 */
export class NloNotAuthenticatedError extends NloError {
	constructor(cause: any) {
		super({ message: NOT_AUTHENTICATED_ERROR_MESSAGE, cause });
	}
}

/**
 * Represents an validation error response from the API.
 */
export class NloApiValidationError extends NloError {
	status: number;
	errors?: {
		[key: string]: readonly string[];
	};

	constructor(apiError: NloApiError) {
		const validationProblemDetails = apiError.data as components['schemas']['ValidationProblemDetails'];

		super({ message: GENERIC_ERROR_MESSAGE });
		this.errors = validationProblemDetails?.errors;
		this.status = apiError.status;
	}
}

export class NloPortalApiError extends NloError {
	status: number;
	problemDetails?: components['schemas']['ErrorResultProblemDetails'];

	constructor(apiError: NloApiError) {
		const problemDetails = apiError.data as components['schemas']['ErrorResultProblemDetails'];
		super({ message: getPortalErrorMessage(problemDetails.errorCode) });
		this.problemDetails = problemDetails;
		this.status = apiError.status;
	}
}

/**
 * Returns the error message for the given error code.
 * Please note that this message is already available through NloApiError's message property.
 * @param errorCode the error code from the C# API.
 * @returns the error message (in Dutch) for the user
 */
function getPortalErrorMessage(errorCode?: components['schemas']['ErrorResultProblemDetails']['errorCode']) {
	switch (errorCode) {
		case 'RateLimitError':
		case 'RecaptchaNotValidError': {
			return 'Je hebt te veel verzoeken in korte tijd verstuurd, vink ter verificatie de recaptcha aan.';
		}
		case 'LookupAddressNotFoundError': {
			return 'Deze postcode/huisnummer combinatie herkennen we niet. Neem contact op met de klantenservice';
		}
		case 'PasswordInvalidError': {
			return 'Het wachtwoord was niet correct';
		}
		case 'AccountNameAlreadyTakenError': {
			return 'Er bestaat al een account met het opgegeven e-mail adres.';
		}
		case 'IbanAlreadyInUseError': {
			return 'Er is al een account met dit IBAN (Rekeningnummer). Neem contact op met onze klantenservice.';
		}
		case 'AccountMustBeValidatedError':
		case 'AccountAlreadyActivatedError':
		case 'AccountNotFoundError':
		case 'ActionExpiredError':
		case 'ActionNotFoundError':
		case 'CannotUpdateAccountError':
		case 'DepositLimitReachedError':
		case 'InvalidPlayerStateError':
		case 'InvalidWithdrawAmountError':
		case 'MaximumNumberOfParticipationsReachedError':
		case 'NotEnoughMoneyError':
		case 'PasswordNotAllowedError':
		case 'UserAlreadyParticipatedError':
		case 'VoucherCodeAlreadyClaimedError':
		case 'VoucherCodeNotFoundError':
		case 'FormSubmitAlreadyParticipatedError':
		case 'FormSubmitValidationError':
		case 'AuthenticationError':
		case 'BankAccountAlreadyInUseError':
		case 'DuplicatePlayerFoundError':
		case 'EmailAlreadyValidatedError':
		case 'InvalidIbanError':
		case 'InvalidValidationCodeError':
		case 'PasswordNotSafeError':
		case 'PasswordTooShortError':
		case 'PoBoxAddressNotAllowedError':
		case 'UnknownEmailValidationException':
		case 'UnknownPlayerError':
		case 'InvalidAccountStatusError':
		case 'ExistingOfflineUserError':
		case undefined: {
			return GENERIC_ERROR_MESSAGE;
		}
		default:
			assertUnreachable(errorCode);
	}
}

/**
 * Represents a network error, for cases where the API can't be reached by the client.
 */
export class NloNetworkError extends NloError {
	constructor(cause: any) {
		super({ message: NETWORK_ERROR_MESSAGE, cause });
	}
}

/**
 * Missing recaptcha error
 */
export class NloMissingRecaptchaError extends NloError {
	constructor(cause?: any) {
		super({ message: RECAPTCHA_MISSING_ERROR_MESSAGE, cause });
	}
}

/**
 * Represents an error generated by our client-side code.
 */
export class NloClientError extends NloError {
	constructor(options: { message?: string; cause?: any; isFatal?: boolean }) {
		super({ ...options, message: options.message || GENERIC_ERROR_MESSAGE });
	}
}

export class NloFailedToParseError extends NloError {
	constructor(cause?: any) {
		super({ message: GENERIC_ERROR_MESSAGE, cause, isFatal: true });
	}
}

/**
 * Represents an unknown error that we couldn't map to anything.
 */
export class NloUnknownError extends NloError {
	constructor(options: { message?: string; cause?: any; isFatal?: boolean }) {
		super({ ...options, message: options.message || GENERIC_ERROR_MESSAGE });
	}
}

/**
 * Represents an aborted request.
 */
export class NloAbortError extends NloError {
	constructor(cause: any) {
		super({ message: ABORT_ERROR_MESSAGE, cause });
	}
}

/**
 * Represents an error when the code on the client is outdated.
 */
export class NloClientOutdatedError extends NloError {
	constructor() {
		super({ message: GENERIC_ERROR_MESSAGE, isFatal: true });
	}
}

/**
 * This represents a redirect from the CMS, so that the user is redirected to the correct page.
 */
export class NloCmsRedirectError extends NloError {
	redirectUrl: string;
	isAbsolute: boolean;

	constructor(redirectUrl: string, isAbsolute: boolean) {
		super({ message: 'message' });
		this.redirectUrl = redirectUrl;
		this.isAbsolute = isAbsolute;
	}
}
